import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  TeamOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePermissions, SCOPES, ACTIONS } from '../../contexts/PermissionsContext';
import MoreMenu from './MoreMenu';
import './BottomNavigation.css';

interface NavItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  paths: string[];
}

const BottomNavigation: React.FC = () => {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const checkAiEnabled = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setAiEnabled(false); return; }
        const base = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${base}/admin-ai/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAiEnabled(data.enabled !== false);
        } else {
          setAiEnabled(false);
        }
      } catch {
        setAiEnabled(false);
      }
    };
    checkAiEnabled();
  }, []);

  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [];

    if (hasPermission(SCOPES.DASHBOARD, ACTIONS.VIEW)) {
      items.push({
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'Home',
        paths: ['/dashboard'],
      });
    }

    if (hasPermission(SCOPES.CLIENTS, ACTIONS.VIEW)) {
      items.push({
        key: '/clients',
        icon: <TeamOutlined />,
        label: t('nav.clients'),
        paths: ['/clients'],
      });
    }

    if (hasPermission(SCOPES.QUOTES, ACTIONS.VIEW)) {
      items.push({
        key: '/quotes',
        icon: <FileTextOutlined />,
        label: t('nav.quotes'),
        paths: ['/quotes'],
      });
    }

    items.push({
      key: 'more',
      icon: <AppstoreOutlined />,
      label: 'Altro',
      paths: ['/products', '/option-groups', '/appointments', '/opportunities'],
    });

    return items;
  }, [hasPermission, t]);

  const hiddenPaths = ['/quotes/new', '/clients/new', '/products/new'];
  const isEditPage = location.pathname.match(/\/(quotes|clients|products)\/\d+\/edit$/);

  if (hiddenPaths.includes(location.pathname) || isEditPage) {
    return null;
  }

  const isActive = (item: NavItem) => {
    return item.paths.some(path => location.pathname.startsWith(path));
  };

  const handleNav = (key: string) => {
    if (key === 'more') {
      setMoreMenuOpen(true);
    } else {
      navigate(key);
    }
  };

  const handleAIClick = () => {
    window.dispatchEvent(new CustomEvent('open-ai-chat'));
  };

  // Dividi gli item con un placeholder al centro per fare spazio al bottone AI
  const midPoint = Math.ceil(navItems.length / 2);
  const leftItems = navItems.slice(0, midPoint);
  const rightItems = navItems.slice(midPoint);

  const renderItem = (item: NavItem) => {
    const active = isActive(item);
    return (
      <button
        key={item.key}
        className={`bottom-nav__item ${active ? 'bottom-nav__item--active' : ''}`}
        onClick={() => handleNav(item.key)}
      >
        <span className="bottom-nav__icon">{item.icon}</span>
        <span className="bottom-nav__label">{item.label}</span>
        {active && <span className="bottom-nav__indicator" />}
      </button>
    );
  };

  return (
    <>
      <nav className="bottom-nav">
        <div className="bottom-nav__container">
          {leftItems.map(renderItem)}

          {/* Placeholder invisibile: riserva spazio al centro per il bottone AI */}
          {aiEnabled && <div className="bottom-nav__ai-placeholder" aria-hidden="true" />}

          {rightItems.map(renderItem)}
        </div>

        {/* Bottone AI: posizionato in assoluto al centro esatto della barra */}
        {aiEnabled && (
          <div className="bottom-nav__ai-center">
            <button
              className="bottom-nav__ai-btn"
              onClick={handleAIClick}
              aria-label="Apri AI Assistant"
            >
              <RobotOutlined style={{ fontSize: 18 }} />
            </button>
            <span className="bottom-nav__ai-label">AI</span>
          </div>
        )}
      </nav>

      <MoreMenu isOpen={moreMenuOpen} onClose={() => setMoreMenuOpen(false)} />
    </>
  );
};

export default BottomNavigation;
